import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { NEXT_MEDIA_PATH } from '@/config/env';
import { ItemTypes, PaymentType } from '@/helpers/constants/shopping';
import { roundToTwoDecimalPlaces } from '@/helpers/utils/numberHelpers';
import { CartItem, EnhancedShoppingCartResponse, ShoppingCartResponse } from '@/types/Cart';
import { CategoryStrapi, CategoryWiseProductsResponse } from '@/types/Category';
import { EzShipsResponse } from '@/types/EzShip';
import { MetaDataBaseStrapi, MetaDataStrapi } from '@/types/MetaData';
import { EnhancedOrderSummaryResponse, GiftCard, Item, OrderSummaryResponse, OrderTotals } from '@/types/OrderSummary';
import { ProductStrapi } from '@/types/Product';
import { ProductGroupMembers, ProductGroupMembersData, StrapiRealProduct } from '@/types/Strapi';

export const shoppingCartAdapter = (shoppingCartResponse: ShoppingCartResponse): EnhancedShoppingCartResponse => {
  const cart = shoppingCartResponse?.data?.cart;
  const giftCardNumber = shoppingCartResponse?.data?.giftCardNumber;
  const couponCode = shoppingCartResponse?.data?.couponCode;
  const qualifiedItemsCount = shoppingCartResponse?.data?.qualifiedItemsCount;
  const shoppingFlowType = shoppingCartResponse?.data?.shoppingFlowType;

  const shoppingCart =
    cart.map(item => ({
      ...item,
      totalPrice: item.quantity * +item.price,
      totalPawPoints: item.quantity * +item.pawPoint,
      totalSampleCredits: item.quantity * +item.sampleCredits,
      weight: `${roundToTwoDecimalPlaces(item.weight)}lb`,
      image: `${NEXT_MEDIA_PATH}${item.image || ''}`,
    })) || ([] as CartItem[]);

  const totalItemsInCart = shoppingCart.reduce((acc, curr) => acc + curr.quantity, 0) || 0;
  const totalPrice =
    roundToTwoDecimalPlaces(
      shoppingCart
        ?.filter(item => item.paymentType === PaymentType.CREDIT_CARD)
        ?.reduce((acc, curr) => +acc + (curr.totalPrice ? +curr.totalPrice : 0), 0)
    ) || 0;

  const totalPawPoints =
    shoppingCart
      ?.filter(item => item.paymentType === PaymentType.PAW_POINT)
      ?.reduce((acc, curr) => +acc + (curr.totalPawPoints ? +curr.totalPawPoints : 0), 0) || 0;

  const totalSampleCredits =
    shoppingCart
      ?.filter(item => item.paymentType === PaymentType.SAMPLE_CREDIT)
      ?.reduce((acc, curr) => +acc + (curr.totalSampleCredits ? +curr.totalSampleCredits : 0), 0) || 0;

  const hasItemWithPawPoints = shoppingCart.some(item => item.paymentType === PaymentType.PAW_POINT);
  const hasItemWithSampleCredits = shoppingCart.some(item => item.paymentType === PaymentType.SAMPLE_CREDIT);
  const ezShipItems = shoppingCart.filter(({ isFreeShipping, isEZShip }) => isFreeShipping && isEZShip);

  const ezShipTotalPrice =
    roundToTwoDecimalPlaces(ezShipItems?.reduce((acc, curr) => +acc + (curr.totalPrice ? +curr.totalPrice : 0), 0)) ||
    0;
  const totalEZShipItems = ezShipItems?.reduce((acc, curr) => acc + curr.quantity, 0) || 0;

  const enhancedShoppingCartResponse: EnhancedShoppingCartResponse = {
    ...shoppingCartResponse,
    data: {
      cart: shoppingCart,
      totalItemsInCart,
      totalPrice,
      totalPawPoints,
      totalSampleCredits,
      hasItemWithSampleCredits,
      hasItemWithPawPoints,
      ezShipItems,
      ezShipTotalPrice,
      totalEZShipItems,
      giftCardNumber,
      couponCode,
      qualifiedItemsCount,
      shoppingFlowType,
    },
  };
  return enhancedShoppingCartResponse;
};

const mapItems = (items: Item[]) => {
  return items?.map(item => ({
    ...item,
    price: roundToTwoDecimalPlaces(item.price),
    totalPrice: item.quantity * +item.price,
    totalPawPoints: item.otherPrice1 * item.quantity,
    weight: `${roundToTwoDecimalPlaces(item.weight)}lb`,
    image: `${NEXT_MEDIA_PATH}${item.imageUrl || ''}`,
    largeImageUrl: `${NEXT_MEDIA_PATH}${item.largeImageUrl || ''}`,
    smallImageUrl: `${NEXT_MEDIA_PATH}${item.smallImageUrl || ''}`,
    tinyImageUrl: `${NEXT_MEDIA_PATH}${item.tinyImageUrl || ''}`,
    isCouponItem: item.itemCode === 'coupon',
    isPcShipDisc: item.itemCode === 'PcShipDisc',
    isPawSamplePoints: item.itemCode === 'PawSamplePoints',
    uniqueId: uuidv4(),
  }));
};

const hasValidShippingAddressAndPaymentMethod = (
  displayPayment: boolean,
  hasValidPaymentMethod: boolean,
  hasGiftCard: boolean,
  autoOrderItems: Item[],
  orderTotals: OrderTotals,
  giftCard: GiftCard
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  let validShippingAddress = false;
  let validPaymentMethod = false;

  if (displayPayment) {
    validShippingAddress = true;

    if (hasGiftCard) {
      const orderTotal = orderTotals?.total || 0;
      const gcBalance = giftCard?.balance || 0;

      if (orderTotal <= gcBalance && !autoOrderItems.length) {
        validPaymentMethod = true;
      } else {
        if (hasValidPaymentMethod) {
          validPaymentMethod = true;
        }
      }
    } else {
      if (hasValidPaymentMethod) {
        validPaymentMethod = true;
      }
    }
  }
  return validPaymentMethod && validShippingAddress;
};

export const orderSummaryAdapter = (orderSummaryResponse: OrderSummaryResponse): EnhancedOrderSummaryResponse => {
  const propertyBag = orderSummaryResponse?.data?.propertyBag;
  const items = orderSummaryResponse?.data?.items;
  const orderTotals = orderSummaryResponse?.data?.orderTotals;
  const shipping = orderSummaryResponse?.data?.shipping;
  const payment = orderSummaryResponse?.data?.payment;

  const allowedShippingAddress =
    propertyBag?.shippingAddress?.state === 'AK' ||
    propertyBag?.shippingAddress?.state === 'HI' ||
    propertyBag?.shippingAddress?.state === '';

  const totalWeight = items?.reduce((acc, curr) => +acc + +curr.weight * curr.quantity, 0);

  const weightCheckout = totalWeight > 2000 && allowedShippingAddress !== true;

  const orderItems = mapItems(items);

  const autoOrderItems = orderItems?.filter(({ type }) => type === ItemTypes.AUTO_ORDER);

  const hasGiftCard = !isEmpty(propertyBag?.giftCard) && !isEmpty(propertyBag?.giftCard?.token);
  const giftCard = propertyBag?.giftCard;

  const displayPayment = !isEmpty(propertyBag?.shippingAddress) && propertyBag?.shippingAddress?.isComplete;
  const hasValidPaymentMethod = !isEmpty(propertyBag?.paymentMethod) && propertyBag?.paymentMethod.isValid;

  const isCostCovered = hasValidShippingAddressAndPaymentMethod(
    displayPayment,
    hasValidPaymentMethod,
    hasGiftCard,
    autoOrderItems,
    orderTotals,
    giftCard
  );

  let remaining: number | string;
  let remainingText: string;

  if (orderTotals?.total > giftCard?.balance) {
    remaining = roundToTwoDecimalPlaces(orderTotals?.total - giftCard?.balance);
    remainingText = 'Total Due';
  } else {
    remaining = roundToTwoDecimalPlaces(giftCard?.balance - orderTotals?.total);
    remainingText = 'Remaining Gift Card Balance';
  }

  const isAutoOrderEmpty = !autoOrderItems?.length;
  const giftCardBalanceIsSufficient = giftCard?.balance >= orderTotals?.total;

  const isCreditCardRequired = true; // displayPayment && !(isAutoOrderEmpty && hasGiftCard && giftCardBalanceIsSufficient);

  const enhancedOrderSummaryResponse: EnhancedOrderSummaryResponse = {
    ...orderSummaryResponse,
    data: {
      shipping,
      propertyBag,
      orderItems,
      autoOrderItems,
      totalOrderItems: orderItems?.reduce((acc, curr) => acc + curr.quantity, 0) || 0,
      totalOrderItemsPrice:
        roundToTwoDecimalPlaces(orderItems?.reduce((acc, curr) => acc + (curr.totalPrice ?? 0), 0)) || 0,
      autoOrderItemsTotalPrice:
        roundToTwoDecimalPlaces(autoOrderItems?.reduce((acc, curr) => acc + (curr.totalPrice ?? 0), 0)) || 0,
      totalAutoOrderItems: autoOrderItems?.reduce((acc, curr) => acc + curr.quantity, 0) || 0,
      displayPayment,
      hasGiftCard,
      giftCard,
      remaining,
      remainingText,
      orderTotals,
      orderSummary: orderSummaryResponse.data,
      weightCheckout,
      isCostCovered,
      isCreditCardRequired,
      giftCardBalanceIsSufficient,
      payment,
    },
  };

  return enhancedOrderSummaryResponse;
};

export const metaDataAdapter = (response: { data: MetaDataStrapi }): MetaDataStrapi => {
  return {
    ...response.data,
    Cat: transformPetData(response.data.Cat, 'cat'),
    Dog: transformPetData(response.data.Dog, 'dog'),
  };
};

const transformPetData = (petData: MetaDataBaseStrapi, specieType: 'cat' | 'dog'): MetaDataBaseStrapi => {
  const shopAllIssue = {
    id: Infinity,
    Name: 'Shop All Issues',
    specieType,
    slug: '',
  };

  const shopAllCategory = {
    id: Infinity,
    Name: 'Shop All Products',
    specieType,
    slug: '',
  };

  return {
    Issues: [shopAllIssue, ...petData.Issues],
    Categories: [shopAllCategory, ...petData.Categories],
  };
};

export const getCustomerEzShipsAdapter = (ezShipsResponse: EzShipsResponse): EzShipsResponse => {
  return {
    ...ezShipsResponse,
    data: ezShipsResponse?.data?.filter(ezShip => !isEmpty(ezShip.title)),
  };
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const flatMapProductsAdapter = (
  data: CategoryWiseProductsResponse
): { Products: ProductStrapi[]; Category: CategoryStrapi } => {
  const Category = {
    Name: data?.attributes?.Name || '',
    Image: data?.attributes?.Image,
    id: data?.id,
    publishedAt: data?.attributes?.publishedAt,
    // eslint-disable-next-line sonarjs/no-redundant-boolean
    NotListableCategory: data?.attributes?.ViewableOnWebsite == false,
  };

  if (data?.attributes?.Products?.data?.length)
    return { ...(productsAdapter(data?.attributes?.Products?.data, data.id) || {}), Category };

  let existingProducts: Record<string, boolean> = {};

  const allCategoriesProducts = data?.attributes?.SubCategories?.data
    ?.sort((a, b) => (a?.attributes?.SortOrder || 0) - (b?.attributes?.SortOrder || 0))
    ?.flatMap(category => productsAdapter(category?.attributes?.Products?.data, category?.id))
    ?.reduce((prev: ProductStrapi[], current) => {
      const { Products } = current;

      return [...prev, ...Products];
    }, [])
    ?.reduce((prev: ProductStrapi[], current) => {
      if (!current?.attributes?.ItemCode || existingProducts[current?.attributes?.ItemCode || '']) return prev;
      existingProducts[current.attributes.ItemCode] = true;

      return [...prev, current];
    }, []);

  // console.log(
  //   JSON.stringify(
  //     allCategoriesProducts.map(a => a?.attributes.SortOrder),
  //     null,
  //     4
  //   ),
  //   'sorted prods'
  // );

  return { Products: allCategoriesProducts, Category };
};

export const productsAdapter = (
  data: StrapiRealProduct[],
  sortByCategoryId?: number
): { Products: ProductStrapi[] } => {
  let products: ProductStrapi[] = [] as ProductStrapi[];

  const realProducts = data || ([] as StrapiRealProduct[]);

  for (let product of realProducts) {
    const { product: productStrapi } = singleProductAdapter(product, sortByCategoryId);
    products.push(productStrapi);
  }

  if (sortByCategoryId)
    products = products.sort(
      (product1, product2) => (product1?.attributes?.SortOrder || 0) - (product2?.attributes?.SortOrder || 0)
    );

  return { Products: products };
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const singleProductAdapter = (
  product: StrapiRealProduct,
  sortByCategoryId?: number
  // eslint-disable-next-line sonarjs/cognitive-complexity
): { product: ProductStrapi } => {
  if (!product) {
    return { product: {} as ProductStrapi };
  }

  const fallbackImage = `${NEXT_MEDIA_PATH}/shopping/productimages/pawtree-Dental-Sticks-7oz.-2619.png`;

  const productMembers: Record<string, boolean> = {};

  const productStrapi: ProductStrapi = {
    id: product?.id,
    attributes: {
      ItemCode: product?.attributes?.ItemDescriptionSetup?.ItemCode || '',
      ItemName: product?.attributes?.ItemDescriptionSetup?.ItemName || '',
      Slug: product?.attributes.Slug,
      Color: product?.attributes?.ItemConfiguration?.Color || '',
      IdealForOrContains: product?.attributes?.ItemDescriptionSetup?.IdealForOrContains,
      HowToUse: product?.attributes?.ItemDescriptionSetup?.HowToUse,
      SEOURL: '',
      pawTreePromise: '',
      ProductDescription: product?.attributes?.ItemDescriptionSetup?.ProductDescription,
      ProductFacts: product?.attributes?.ItemDescriptionSetup?.ProductFacts,
      KeyWordsForSearch: product?.attributes?.ItemConfiguration?.KeyWordsForSearch,
      Size: product?.attributes?.ItemConfiguration?.Size || '',
      TruckIconFreeShippingQualified: product?.attributes?.ItemConfiguration?.TruckIconFreeShippingQualified || '',
      pawPointType: product?.attributes?.ItemConfiguration?.pawPointType || '',
      PetCategory: product?.attributes?.ItemConfiguration?.PetCategory || '',
      LabelEligible: '',
      Weight: product?.attributes?.ItemDescriptionSetup?.Weight || '',
      SuppressGroupMaster: '',
      EZShipEligible: product?.attributes?.ItemConfiguration?.EZShipEligible || false,
      EnrollmentItem: product?.attributes?.ItemConfiguration?.EnrollmentItem || false,
      EnrollmentProductPackEligible: product?.attributes?.ItemConfiguration?.EnrollmentProductPackEligible || false,
      EnrollmentShortInfo: '',
      ItemAvalibility: null,
      PointsIncrement: product?.attributes?.ItemConfiguration?.PointsIncrement || '',
      Ingredients: product?.attributes?.ItemDescriptionSetup?.Ingredients,
      ProductVideo1: product?.attributes?.ItemDescriptionSetup?.ProductVideo1,
      ProductVideo2: product?.attributes?.ItemDescriptionSetup?.ProductVideo2,
      createdAt: '',
      updatedAt: '',
      publishedAt: product?.attributes?.publishedAt,
      URL_: null,
      Metadata_: null,
      CanonicalTags_: null,
      MetaDescription_: null,
      ImageAltText_: null,
      OutOfStock: product?.attributes?.ItemConfiguration?.OutOfStock || false,
      InvoiceImage: product?.attributes?.ItemDescriptionSetup?.InvoiceImage?.data?.attributes?.url || fallbackImage,
      MainImage: product?.attributes?.ItemDescriptionSetup?.MainImage?.data?.attributes?.url || fallbackImage,
      SmallImage: product?.attributes?.ItemDescriptionSetup?.SmallImage?.data?.attributes?.url || fallbackImage,
      AdditionalImage1: product?.attributes?.ItemDescriptionSetup?.AdditionalImage1?.data?.attributes?.url || '',
      AdditionalImage2: product?.attributes?.ItemDescriptionSetup?.AdditionalImage2?.data?.attributes?.url || '',
      AdditionalImage3: product?.attributes?.ItemDescriptionSetup?.AdditionalImage3?.data?.attributes?.url || '',
      AdditionalImage4: product?.attributes?.ItemDescriptionSetup?.AdditionalImage4?.data?.attributes?.url || '',
      AdditionalImage5: product?.attributes?.ItemDescriptionSetup?.AdditionalImage5?.data?.attributes?.url || '',
      AdditionalImage6: product?.attributes?.ItemDescriptionSetup?.AdditionalImage6?.data?.attributes?.url || '',
      Prices: product?.attributes?.Prices,
      Grouping: product?.attributes?.Grouping,
      TrustPilotLastSyncDate: product?.attributes?.TrustPilotLastSyncDate,
      ProductGroupMembers: {
        ...(product?.attributes?.ProductGroupMembers || {}),
        data: product?.attributes?.ProductGroupMembers?.data
          ?.filter(
            (member: ProductGroupMembersData) =>
              product?.attributes?.ItemDescriptionSetup?.ItemCode !==
              member?.attributes?.Product?.data?.attributes?.ItemDescriptionSetup?.ItemCode
          )
          ?.reduce((prev: ProductGroupMembers['data'], member) => {
            if (
              !member?.attributes?.Product?.data?.attributes?.ItemDescriptionSetup?.ItemCode ||
              productMembers[member?.attributes?.Product?.data?.attributes?.ItemDescriptionSetup?.ItemCode || '']
            )
              return prev;
            productMembers[member.attributes.Product.data.attributes.ItemDescriptionSetup.ItemCode] = true;

            return [...prev, member];
          }, []),
      },

      ...(sortByCategoryId
        ? {
            SortOrder: +(
              product?.attributes?.CategorySortOrders?.data?.find(
                sorOrder =>
                  sorOrder?.attributes?.Categories?.data?.find(cat => cat?.id === sortByCategoryId) &&
                  sorOrder?.attributes?.Products?.data?.find(prod => prod?.id === product.id)
              )?.attributes.SortOrder || '9999'
            ),
          }
        : {}),
      IconForPawClubPromotion:
        product?.attributes?.ItemDescriptionSetup?.IconForPawClubPromotion?.data?.attributes?.url || '',
      PromotionFeature: product?.attributes?.ItemDescriptionSetup?.PromotionFeature,
      FrequentlyAskedQuestions: product?.attributes?.ItemDescriptionSetup?.FrequentlyAskedQuestions,
      FoodTransitionInstructions: product?.attributes?.ItemDescriptionSetup?.FoodTransitionInstructions,
      FoodTransitionInstructionsForMobile:
        product?.attributes?.ItemDescriptionSetup?.FoodTransitionInstructionsForMobile,
      SEOKeywords: product?.attributes?.ItemDescriptionSetup?.SEOKeywords,
      SEOMetadata: product?.attributes?.ItemDescriptionSetup?.SEOMetadata,
      Metadata: product?.attributes?.ItemDescriptionSetup?.Metadata,
      PromotionPDF: product?.attributes?.ItemDescriptionSetup?.PromotionPDF,
      ItemPDF: product?.attributes?.ItemDescriptionSetup?.ItemPDF,
      ShortProductDescription: product?.attributes?.ItemDescriptionSetup?.ShortProductDescription,
      // eslint-disable-next-line sonarjs/no-redundant-boolean
      ShowIngredients: true ?? product?.attributes?.ItemDescriptionSetup?.ShowIngredients ?? false,
      ShowTrustpilotReviews: product?.attributes?.ItemDescriptionSetup?.ShowTrustpilotReviews ?? false,
      FeaturedIngredients: product?.attributes?.ItemDescriptionSetup?.FeaturedIngredients,
      OtherIngredients: product?.attributes?.ItemDescriptionSetup?.OtherIngredients,
      // eslint-disable-next-line sonarjs/no-redundant-boolean
      DisableCartOperations: product?.attributes?.ViewableOnWebsite == false,
      // eslint-disable-next-line sonarjs/no-redundant-boolean
      RedirectToHomepage: product?.attributes?.ViewableOnWebsite == false,
      Categories: product?.attributes?.Categories,
      slugOfGMThisProductBelongsTo: product?.slugOfGMThisProductBelongsTo,
      isSubProductOfGM: product?.isSubProductOfGM,
      currentProductSlug: product?.currentProductSlug,

      isRootGMOfGM: product?.isRootGMOfGM,
      slugOfRootGMThisGMBelongsTo: product?.slugOfRootGMThisGMBelongsTo,
      currentRootGMProductSlug: product?.currentRootGMProductSlug,
    },
  };

  if (product?.categoryId) {
    productStrapi.categoryId = product.categoryId;
  }

  return { product: productStrapi };
};
